import { type FirebaseApp, initializeApp } from "firebase/app";
import { getMessaging, getToken, isSupported, type Messaging, onMessage } from "firebase/messaging";

import { DebugLogger } from "~/lib/log";
import { dispatchAnalyticsTrack } from "~/lib/segment";
import { inCalendarMode, inVideocallMode } from "~/lib/utils";

import notificationIcon from "./assets/notification-icon.png";
import {
	commitNewToken,
	DeviceContext,
	getCollapseKey,
	NotificationsImpl,
	openNotificationURL,
	showPermissionRequestToast,
	type WebNotificationPayload,
} from "./shared";

const logger = new DebugLogger("[Notifications.web]");
const log = logger.log;
const firebaseConfig = window.FIREBASE_CONFIG;

function getSw(): ServiceWorkerContainer | undefined {
	const { serviceWorker } = window.navigator;
	if (serviceWorker === undefined) {
		logger.warn("navigator.serviceWorker is undefined", new Error().stack);
	}
	return serviceWorker;
}

export default class WebImpl extends NotificationsImpl {
	public readonly platform: DeviceContext = DeviceContext.WEB;

	private static firebaseApp?: FirebaseApp;
	private messaging: Messaging | undefined;

	private deliveredNotifications: Record<string, Notification> = {};

	notificationPermission(): Promise<(typeof Notification)["permission"]> {
		// eslint-disable-next-line compat/compat
		return Promise.resolve(Notification.permission);
	}

	/**
	 * We only want this to be initialized once even if the NotificationProvider
	 * rerenders (this causes an error since the app is already initialized), but
	 * we still don't want to initialize it on page load. Since this is the case
	 * we'll just lazily initialize it the first time it's asked for.
	 */
	private static ensureInitialized(config: NonNullable<typeof firebaseConfig>) {
		if (WebImpl.firebaseApp) {
			log("Firebase already initialized, not initializing again");
			return;
		}

		WebImpl.firebaseApp = initializeApp(config);
		log("Initialized Firebase app");
	}

	async addEventListeners(): Promise<void> {
		if (!(await isSupported()) || !firebaseConfig) {
			log("Not supported or missing config");
			return;
		}

		const sw = getSw();
		if (!sw) return;

		await sw.ready;
		log("Service worker is ready");

		WebImpl.ensureInitialized(firebaseConfig);
		this.messaging = getMessaging(WebImpl.firebaseApp);

		onMessage(this.messaging, message => {
			log("Got notification message", message);
			const payload = { data: message.data } as unknown as WebNotificationPayload;

			const key = getCollapseKey(payload);
			if (!key || !this.idIsCleared(key)) {
				let body = payload.data.body;
				if (payload.data.subtitle) {
					body = `${payload.data.subtitle}\n${body}`;
				}

				// can't use capacitor LocalNotifications plugin since we wouldn't be able
				// to add any extra metadata to the notification (like the url)
				const notif = new Notification(payload.data.title, {
					body,
					tag: payload.data.tag ?? undefined,
					icon: payload.data.actor_picture_url,
					badge: notificationIcon,
					data: payload.data,
				});

				const url = payload.data.url;
				notif.onclick = () => {
					log("Notification clicked", { sw: false });
					if (typeof url === "string") {
						openNotificationURL(url);
					}
				};

				if (key) {
					this.clearNotificationThread(key);
					this.deliveredNotifications[key] = notif;
				}
			} else {
				log("Notification matches outerObjectContextId, not sending");
			}
		});

		window.addEventListener("focus", this.onWindowFocus);

		sw.removeEventListener("message", this.onSWMessage);
		sw.addEventListener("message", this.onSWMessage);
	}

	destroy(): void {
		window.removeEventListener("focus", this.onWindowFocus);
		getSw()?.removeEventListener("message", this.onSWMessage);
	}

	private onWindowFocus = () => {
		log("Window focused", { clearIds: this.clearIds });
		if (this.clearIds.size) {
			for (const id of this.clearIds) {
				this.clearNotificationThread(id);
			}
		}
	};

	private onSWMessage = (event: MessageEvent) => {
		if (typeof event.data !== "object" || event.data === null) {
			return;
		}

		const { action, data } = event.data;

		if (action === "notificationClick" && typeof data === "object" && data) {
			log("Notification clicked", { sw: true, payload: data });

			const { url } = data;

			openNotificationURL(url);
		}
	};

	private async requestPlatformPermission() {
		// eslint-disable-next-line compat/compat
		const permission = await Notification.requestPermission();
		if (permission === "granted") {
			dispatchAnalyticsTrack("Notification Permission Enabled");
		}
		this.notificationsRef.register(this);
	}

	async requestPermission({
		message = "Fellow needs your permission to enable push notifications.",
		cta = "Enable",
		skipToast = false,
	}) {
		if (inVideocallMode || inCalendarMode) return;

		// eslint-disable-next-line compat/compat
		const permission = Notification.permission;
		log("Current permission", { permission });

		if (permission === "default" && this.platform === DeviceContext.WEB) {
			if (skipToast) {
				this.requestPlatformPermission();
				return;
			}

			showPermissionRequestToast({
				cta,
				message,
				onEnable: this.requestPlatformPermission.bind(this),
			});
		}
	}

	protected async doRegister(deviceId: string, platform: string): Promise<void> {
		if (!(await isSupported())) return;

		// eslint-disable-next-line compat/compat
		const permission = Notification.permission;
		log("Current permission", { permission });

		if (permission === "granted") {
			if (!this.messaging) {
				return;
			}

			const sw = getSw();
			if (!sw) return;

			const registration = await sw.getRegistration();
			if (!registration) {
				throw new Error("No service worker registration when initializing firebase");
			}

			getToken(this.messaging, { serviceWorkerRegistration: registration }).then(newToken => {
				log("Got new token", { token: newToken });
				commitNewToken(deviceId, platform, newToken);
			});
		} else if (permission === "default" && this.platform === DeviceContext.WEB) {
			if (inVideocallMode || inCalendarMode) return;

			showPermissionRequestToast({
				cta: "Enable",
				message: "Fellow needs your permission to enable desktop notifications",
				onEnable: this.requestPlatformPermission.bind(this),
			});
		}
	}

	clearNotificationThread(id: string): Promise<void> {
		const message = {
			action: "clearNotification",
			data: id,
		};

		log("Clearing notification thread", { id });

		getSw()?.controller?.postMessage(message);
		this.deliveredNotifications[id]?.close();

		return Promise.resolve();
	}
}
